import { createLogger } from 'shared/services/logger';

const logger = createLogger('UScope:extensions:annotations:RegionManager');

// ________________________________________________________________________________________________
// regions.js
/**
 * @file   example_action.h
 * @Author Me (me@example.com)
 * @date   September, 2008
 * @brief  Brief description of file.
 *
 * Detailed description of file.
 */
//    This file contains the annotation region collection object 'RegionList'.
//
//    history
//
//    1302    cperz    created
//    _______________________________________end_AIHv1/PR __________________________________________
//    141118    cperz    ol3 integration
//    141222    cperz    RegionList created; move Region (singular) into its own file; add support for image analysis:
//    150318    cperz    build out for analysis and markup;
//
// ________________________________________________________________________________________________
//    Object Name: RegionList
//    Overview:  The RegionList object is a container for Region objects

export default function cRegions (events, annotationsService) {
  var self = this;

  // ..... to group fns in js parser, for ease of search (e.g. search for all 'us_region_pts')
  var CREGIONS = this;
  var CREGIONS_GET = this;
  var CREGIONS_LAYER = this;
  var CREGIONS_MARKUP = this;
  var CREGIONS_ANALYSIS = this;

  // The array containing all regions, rfId (live assigned) is the index into the array (not regionId)
  // Note that the length of this array is NOT the number of regions.  Deleted regions are undefined slots in the array.
  const rfRegions = [];
  CREGIONS_GET.getRegionsArray = function () {
    return rfRegions;
  };

  var isValidRegion_ = function (rfId) {
    return !!rfRegions[rfId];
  };

  CREGIONS_GET.filter = (delegate) => {
    return rfRegions.filter(delegate);
  };

  CREGIONS_GET.find = (delegate) => {
    return rfRegions.find(delegate);
  };

  CREGIONS_GET.getRegionId = function (rfId) {
    return isValidRegion_(rfId) ? rfRegions[rfId].getRegionId() : undefined;
  };

  CREGIONS_GET.getType = function (rfId) {
    return isValidRegion_(rfId) ? rfRegions[rfId].getType() : undefined;
  };
  CREGIONS_GET.getShape = function (rfId) {
    return isValidRegion_(rfId) ? rfRegions[rfId].getShape() : undefined;
  };
  CREGIONS_GET.getLayerId = function (rfId) {
    return isValidRegion_(rfId) ? rfRegions[rfId].getLayerId() : -1;
  }; // return -1 if region does not exist
  CREGIONS_GET.getRGB = function (rfId) {
    return isValidRegion_(rfId) ? rfRegions[rfId].getRGB() : undefined;
  };

  CREGIONS_ANALYSIS.isAnalyzed = function (rfId) {
    return isValidRegion_(rfId) ? rfRegions[rfId].isAnalyzed : false;
  };
  CREGIONS_GET.isWholeSlide = function (rfId) {
    return isValidRegion_(rfId) ? rfRegions[rfId].isWholeSlide : false;
  };
  CREGIONS_MARKUP.isWholeSlideMarkup = function (rfId) {
    return isValidRegion_(rfId) ? rfRegions[rfId].isWholeSlideMarkup : false;
  };

  CREGIONS_MARKUP.markupIsInView = function (rfId, cr2View) {
    return isValidRegion_(rfId) ? rfRegions[rfId].markupIsInView(cr2View) : false;
  };
  CREGIONS_MARKUP.getMarkupInfo = function (rfId) {
    return isValidRegion_(rfId) ? rfRegions[rfId].getMarkupInfo() : undefined;
  };
  CREGIONS_MARKUP.hasMarkup = function (rfId) {
    return isValidRegion_(rfId) ? rfRegions[rfId].get('hasMarkup') : false;
  };

  // ............................................ RegionList getReportRegionId
  CREGIONS_GET.getReportRegionId = function () { // note: this is inefficient if there are a lot of regions...
    for (var r = 0, len = rfRegions.length; r < len; r++) {
      if (rfRegions[r] && rfRegions[r].get('isReportRegion')) { return rfRegions[r].get('regionId'); }
    }
    return -1;
  };

  /**
   * Updates region info
   * @param json ({ imageId: imageId, negId: oRegionId, assignedId: regionId, layerId: layerId })
   */
  CREGIONS.addRegionResponse = function (json) {
    const layer = annotationsService.layers.getLayerById(Number(json.layerId));
    const region = layer ? layer.getRegion(Number(json.negId)) : null;
    if (region) {
      const posId = Number(json.assignedId);
      const oldRegionId = region.regionId;

      region.setRegionId(posId);

      events.trigger('us:annotations:region-change', {
        imageId: json.imageId,
        regionId: posId,
        oldRegionId,
        region,
        anShape: (region.isReportRegion) ? 'report' : region.shape,
        action: 'add'
      });
    } else {
      logger.error('addRegionResponse:  imageId: ' + json.imageId + '  assigned id: ' + json.assignedId + ' not reconciled with temp id: ' + json.negId);
    }
  };

  CREGIONS.clear = function () {
    rfRegions.splice(0);
  };

  CREGIONS.add = async function (aRegion, shouldSave = false) {
    rfRegions[aRegion.rfId] = aRegion;

    events.trigger('us:annotations:region-added', aRegion);

    if (shouldSave) {
      const viewport = annotationsService.getMyImage();

      // The "before save" event can (and is) used as a "new region added to viewport" event, as distinct from
      // existing regions simply being drawn. It would be nice to have a "new region created" event.
      viewport.events.trigger('us:annotations:before-new-region-save', aRegion);

      await annotationsService.save({
        action: 'addRegion',
        layerId: aRegion.layerId,
        regionXML: aRegion.buildXML(true),
        tag: aRegion.regionId
      });

      viewport.events.trigger('us:annotations:new-region-saved', aRegion);
    }
  };

  //    remove a single region and delete it from the database
  CREGIONS.del = async function (rfId) {
    const region = rfRegions[rfId];
    if (rfId < 0 || !region) {
      logger.warn('CREGIONS.del: could not locate rfid', rfId);
      return Promise.resolve(false);
    }

    self.remove(rfId); // remove region from rfRegions

    await annotationsService.save({
      action: 'delRegion',
      layerId: region.layerId,
      regionId: region.regionId
    });

    events.trigger('us:annotations:region-deleted', region);

    // broadcast the region deletion last, it is important that it no longer can be referenced in rfRegions
    events.trigger('us:annotations:region-change', {
      action: 'del',
      imageId: region.imageId,
      regionId: region.regionId,
      anShape: (region.isReportRegion) ? 'report' : region.shape
    });

    return Promise.resolve(true);
  };

  // ............................................ RegionList delByRegionId
  CREGIONS.delByRegionId = function (regionId) {
    var aRegion = self.getByRegionId(regionId);
    if (aRegion) {
      self.del(aRegion.get(aRegion.get('rfId')));
    }
  };

  self.removeAllFromLayer = function (layerId) {
    rfRegions
      .filter(r => r.layerId == layerId)
      .forEach(r => self.remove(r.rfId));
  };

  // ............................................ RegionList remove
  //    This function removes a single region from rfRegions and its graphics dependencies.
  //        It does not update the database
  CREGIONS.remove = function (rfId) {
    const region = rfRegions[rfId];
    if (rfId < 0 || !rfRegions[rfId] || rfRegions[rfId].get('isTuningRegion')) {
      logger.log('CREGIONS.remove: Could or would not delete rfid', rfId);
      return false;
    }

    region.remove();

    delete rfRegions[rfId];

    events.trigger('us:annotations:region-removed', region);

    return true;
  };
  // ............................................ delAllFromLayer
  //    delete all regions in the passed layerId
  // WARNING: DO NOT USE THIS FUNCTION. WHILE IT APPEARS CONVENIENT AND GENERAL PURPOSE IT CAN POTENTIALLY
  //          HUNDREDS OR THOUSANDS OF HTTP REQUESTS TO THE SERVER. IT IS CURRENTLY ONLY USED TO DELETE REPORT REGIONS
  //          WHICH IS A UNIQUE CASE. TODO: REWORK AND REMOVE.
  CREGIONS_LAYER.delAllFromLayer = function (layerId) {
    for (var r = 0, len = rfRegions.length; r < len; r++) {
      if (rfRegions[r] && rfRegions[r].layerId == layerId) {
        self.del(rfRegions[r].rfId);
      }
    }
  };

  // ............................................ getRegionsByLayerId
  // return an array of regions refs associated with the passed layerId
  CREGIONS_LAYER.getRegionsByLayerId = function (layerId) {
    var rtnArray = [];
    for (var r = 0, rlen = rfRegions.length; r < rlen; r++) {
      if (rfRegions[r] && rfRegions[r].get('layerId') == layerId) { rtnArray.push(rfRegions[r]); }
    }
    return rtnArray;
  };
  // ............................................ RegionList findByProperty
  // This function returns an array of region objects whose property matches the passed value
  CREGIONS.findByProperty = function (propName, propValue) {
    var rtnArray = [];
    for (var r = 0, rlen = rfRegions.length; r < rlen; r++) {
      if (rfRegions[r] && rfRegions[r].get(propName) == propValue) { rtnArray.push(rfRegions[r]); }
    }
    return rtnArray;
  };
  // ............................................ getByProperty
  CREGIONS_GET.getByProperty = function (propName, propValue) {
    var i, len;
    for (i = 0, len = rfRegions.length; i < len; i++) {
      if (rfRegions[i] && rfRegions[i].get(propName) == propValue) { return rfRegions[i]; }
    }
    return undefined;
  };
  // ............................................ getByRegionId
  // return a ref to the region with passed regionId or undefined if not found
  CREGIONS_GET.getByRegionId = function (regionId) {
    return rfRegions.find(r => r && r.regionId == regionId);
  };
  // ............................................ getByRfId
  // return a ref to the region with passed rfId or undefined if not found
  CREGIONS_GET.getByRfId = function (rfId) {
    return rfRegions[rfId];
  };
}



// WEBPACK FOOTER //
// ./src/uScope/js/extensions/annotations/regions/regions.js